// Helper functions for form type conversions

export const convertToString = (
  value: string | boolean | null | undefined
): string => {
  if (value === null || value === undefined) return "";
  return value.toString();
};

export const convertToBoolean = (value: string): boolean | null => {
  if (value === "true") return true;
  if (value === "false") return false;
  if (value === "null") return null;
  return null;
};

export const safeStringValue = (value: string | null | undefined): string => {
  return value ?? "";
};

// Convert Option arrays to string-only values for shadcn components
export const convertOptionsToString = (
  options: Array<{ label: string; value: string | boolean }>
) => {
  return options.map((opt) => ({
    label: opt.label,
    value: opt.value.toString(),
  }));
};

// Wrapper for Input field props to handle null values
export const safeInputProps = (field: any) => ({
  ...field,
  value: safeStringValue(field.value),
});

// Wrapper for Select field props to handle null values
export const safeSelectProps = (field: any) => ({
  ...field,
  value: safeStringValue(field.value),
});

export const NEW_REQUEST_FIELD_LABELS: Record<string, string> = {
  type: "Request type",
  practice_id: "Practice",
  billing_provider_id: "Billing provider",
  rendering_provider_id: "Rendering provider",
  desired_completion_date: "Results needed by",
  patient_name: "Patient name",
  dob: "Date of birth",
  insurance_name: "Insurance",
  insurance_in_network: "In-network coverage",
  claims_date_of_service: "Date of service",
  billed_amount: "Billed amount",
  additional_info: "Additional info",
  place_of_service: "Place of service",
  is_specialist: "Specialist",
  procedure_codes: "Procedure codes",
  call_script_id: "Call script",
  member_id: "Member ID",
  group_id: "Group ID",
  diagnosis_codes: "Diagnosis codes",
};

function humanizeFieldPath(path: string): string {
  const lastSegment = path.split(".").pop() || path;
  return lastSegment.replace(/_/g, " ");
}

function labelForPath(path: string, labels: Record<string, string>): string {
  if (labels[path]) {
    return labels[path];
  }
  const parts = path.split(".");
  for (let i = parts.length; i > 0; i -= 1) {
    const candidate = parts.slice(0, i).join(".");
    if (labels[candidate]) {
      return labels[candidate];
    }
  }
  const lastSegment = parts[parts.length - 1] || path;
  if (labels[lastSegment]) {
    return labels[lastSegment];
  }
  return humanizeFieldPath(path.replace(/\.\d+$/, ""));
}

function formatFieldErrorMessage(
  path: string,
  message: string,
  labels: Record<string, string>
): string {
  const lastSegment = path.split(".").pop() || path;
  const label = labelForPath(path, labels);
  const genericRequired = `${lastSegment} is a required field`;
  if (
    !message ||
    message.toLowerCase() === genericRequired.toLowerCase() ||
    message.toLowerCase() === "required"
  ) {
    return `${label} is required`;
  }
  if (message.toLowerCase().includes(label.toLowerCase())) {
    return message;
  }
  return `${label}: ${message}`;
}

/** Flatten react-hook-form / yup FieldErrors into user-facing messages. */
export function collectFormErrorMessages(
  errors: unknown,
  labels: Record<string, string> = {}
): string[] {
  const messages: string[] = [];

  const walk = (value: unknown, path: string) => {
    if (value == null || typeof value !== "object") {
      return;
    }
    const record = value as Record<string, unknown>;
    if (typeof record.message === "string" && record.message) {
      messages.push(formatFieldErrorMessage(path, record.message, labels));
      return;
    }
    for (const [key, child] of Object.entries(record)) {
      if (key === "ref" || key === "type" || key === "types") {
        continue;
      }
      walk(child, path ? `${path}.${key}` : key);
    }
  };

  walk(errors, "");
  return messages;
}
